import toast from 'react-hot-toast';
import { loadWorkflowLastJobId, loadWorkflowSessions } from './workflowSessionStorage';
import { loadTabMetadata, loadTabFromSession } from './tabStorage';
import { normalizeWorkflowData, getWorkflowDeclarations } from './workflowDataUtils';
import { setEditableData, updateWorkflowSessionState, clearValidationErrors, setDeclarationDirty } from '../store/reducers';
import { getWorkflowFiles } from './workflowFileCache';
import { parseProcessingErrorMessage } from './processingErrorUtils';

// validates extracted data structure
export const validateExtractedData = (data) => {
  if (Array.isArray(data)) {
    return data.length > 0 && data.every(d => 
      d !== null && 
      d !== undefined && 
      typeof d === 'object' && 
      Object.keys(d).length > 0
    );
  }
  return data && typeof data === 'object' && Object.keys(data).length > 0;
};

// fetches a job by ID from the API
export const fetchJobById = async (jobId, token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/queue/${jobId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'accept': 'application/json'
      }
    });

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    return null;
  }
};

// fetches the current job from the API
export const fetchCurrentJob = async (token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/queue/current`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'accept': 'application/json'
      }
    });

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    return null;
  }
};

// fetches all jobs and finds the most recent completed job
export const fetchMostRecentCompletedJob = async (token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/queue`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      return null;
    }

    const allJobs = await response.json();
    
    if (!Array.isArray(allJobs) || allJobs.length === 0) {
      return null;
    }

    // Sort by updatedAt descending to get most recent first
    const sortedJobs = allJobs.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA;
    });
    
    // Find first completed job with result
    const completedJob = sortedJobs.find(job => 
      job.status === 'completed' && 
      job.result && 
      (Array.isArray(job.result) ? job.result.length > 0 : Object.keys(job.result || {}).length > 0)
    );
    
    return completedJob || null;
  } catch (error) {
    return null;
  }
};

// validates and normalizes job result data
export const validateAndNormalizeJobResult = (result) => {
  if (Array.isArray(result)) {
    const filtered = result.filter(r => 
      r !== null && 
      r !== undefined && 
      typeof r === 'object' && 
      Object.keys(r).length > 0
    );
    return filtered.length > 0 ? filtered : null;
  } else if (result && typeof result === 'object' && Object.keys(result).length > 0) {
    return [result];
  }
  return null;
};

export const restoreWorkflowData = async ({
  workflowSessionId,
  dispatch,
  setExtractedData,
  setStepsCompleted,
  setOcrInputID,
  setShowRestoreButton
}) => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to restore data');
      return { success: false, error: 'No token' };
    }

    // STEP 1: Check if editableData exists in workflow session state in sessionStorage This is the primary source - if editableData exists here, restore it and skip API calls entirely
    if (workflowSessionId) {
      const workflowSessions = loadWorkflowSessions();
      if (workflowSessions && workflowSessions.sessions && workflowSessions.sessions[workflowSessionId]) {
        const workflowSession = workflowSessions.sessions[workflowSessionId];
        const sessionState = workflowSession.state;
        
        if (sessionState) {
          // Get declarations from workflow session state (prioritizes editableData over extractedData)
          const declarations = getWorkflowDeclarations(sessionState);
          
          if (declarations && declarations.length > 0) {
            // CRITICAL: Clear stale validation errors - they will be recalculated based on restored data
            dispatch(updateWorkflowSessionState(workflowSessionId, { validationErrors: [] }));
            
            // Restore the data to Redux
            dispatch(setExtractedData(declarations));
            dispatch(setEditableData(declarations));
            dispatch(setStepsCompleted(true));
            setShowRestoreButton(false);
            
            // Restore OCR input ID if available
            if (sessionState.ocrInputID) {
              dispatch(setOcrInputID(sessionState.ocrInputID));
            }
            
            toast.success('Data restored successfully from saved session');
            return { success: true, data: declarations, restoredFromWorkflowSession: true };
          }
        }
      }
    }

    // STEP 2: Check if editableData exists in sessionStorage (tabs) This is the secondary source
    const storedMetadata = loadTabMetadata();
    if (storedMetadata && storedMetadata.length > 0) {
      // Check if any tab has editableData
      const hasEditableData = storedMetadata.some(tabMeta => {
        const tabData = loadTabFromSession(tabMeta.tabId);
        return tabData && tabData.editableData;
      });

      if (hasEditableData) {
        // We have editableData in tabs - restore from tabs (handled by caller)
        console.log(`[Workflow:${workflowSessionId}] Found editableData in tabs, restoring from tabs`);
        return { success: true, restoredFromTabs: true };
      }
    }

    // STEP 3: No editableData found in sessionStorage - check job status via API Get workflow-specific jobId
    const workflowJobId = workflowSessionId ? loadWorkflowLastJobId(workflowSessionId) : null;
    
    if (!workflowJobId) {
      throw new Error('No job ID found for this workflow');
    }

    // Fetch job status from API
    const jobData = await fetchJobById(workflowJobId, token);
    
    if (!jobData) {
      throw new Error('Job not found');
    }

    // Check for error status
    if (jobData?.status === 'error' && jobData?.code === 'NOT_FOUND') {
      throw new Error('Job not found');
    }

    // STEP 4: Handle based on job status
    if (jobData.status === 'completed' && jobData.result) {
      // Job is completed - use result data
      const validResult = validateAndNormalizeJobResult(jobData.result);
      
      if (validResult && validResult.length > 0) {
        dispatch(setExtractedData(validResult));
        if (validResult[0]?.ocr_input_id) {
          dispatch(setOcrInputID(validResult[0].ocr_input_id));
          // CRITICAL: Also update workflow session state to ensure ocrInputID persists across refreshes
          if (workflowSessionId) {
            dispatch(updateWorkflowSessionState(workflowSessionId, {
              ocrInputID: validResult[0].ocr_input_id,
            }));
          }
        }
        dispatch(setStepsCompleted(true));
        setShowRestoreButton(false);
        toast.success('Processed data restored successfully from completed job');
        return { success: true, data: validResult, restoredFromJob: true };
      }
    } else if (jobData.status === 'processing' || jobData.status === 'queued' || jobData.status === 'pending') {
      // Job is still processing - return info to restore stream
      console.log(`[Workflow:${workflowSessionId}] Job ${workflowJobId} is still processing, should restore stream`);
      return { 
        success: true, 
        shouldRestoreStream: true, 
        jobId: workflowJobId,
        jobStatus: jobData.status
      };
    } else if (jobData.status === 'failed' || jobData.status === 'error') {
      throw new Error(parseProcessingErrorMessage(jobData.error || jobData.message || 'Unknown error'));
    } else {
      throw new Error(`Unknown job status: ${jobData.status}`);
    }

    // If we get here, nothing worked
    throw new Error('No data available to restore');
  } catch (error) {
    console.error(`[Workflow:${workflowSessionId}] Error restoring workflow data:`, error);
    setShowRestoreButton(false);
    toast.error(error.message || 'No data available to restore');
    return { success: false, error: error.message };
  }
};

// Legacy restore function - kept for backward compatibility restores processed data from sessionStorage or API
export const restoreProcessedData = async ({
  dispatch,
  setExtractedData,
  setStepsCompleted,
  setOcrInputID,
  setShowRestoreButton
}) => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to restore data');
      return { success: false, error: 'No token' };
    }

    const lastJobId = sessionStorage.getItem('lastJobId');
    
    // First, try to restore from active workflow's tabs (source of truth) Tabs contain the editableData for each workflow NOTE: originalExtractedData is deprecated - use workflow tabs instead
    const originalExtractedDataStr = null; // Deprecated - use tabs instead
    if (originalExtractedDataStr) {
      try {
        const originalData = JSON.parse(originalExtractedDataStr);
        if (validateExtractedData(originalData)) {
          dispatch(setExtractedData(originalData));
          dispatch(setStepsCompleted(true));
          sessionStorage.removeItem('documentReadyReceived');
          setShowRestoreButton(false);
          toast.success('Processed data restored successfully from saved data');
          return { success: true, data: originalData };
        }
      } catch (e) {
        // Invalid JSON, continue to API fetch
      }
    }
    
    // If no originalExtractedData, try to fetch from API Priority: 1) Fetch by job ID (most reliable), 2) /api/queue/current, 3) /api/queue (all jobs)
    let currentJobData = null;
    
    // First, if we have a lastJobId, try fetching that specific job (most reliable)
    if (lastJobId) {
      currentJobData = await fetchJobById(lastJobId, token);
    }
    
    // If fetching by ID didn't work, try /api/queue/current
    if (!currentJobData) {
      currentJobData = await fetchCurrentJob(token);
    }
    
    // If still no job, try getting all jobs and find the most recent completed one
    if (!currentJobData) {
      currentJobData = await fetchMostRecentCompletedJob(token);
    }

    // If we have job data, try to restore from it
    if (currentJobData) {
      // Check for specific error format
      if (currentJobData?.status === 'error' && currentJobData?.code === 'NOT_FOUND') {
        throw new Error('Job not found');
      }

      // If job is completed, restore the data
      if (currentJobData.status === 'completed' && currentJobData.result) {
        const validResult = validateAndNormalizeJobResult(currentJobData.result);
        
        if (validResult && validResult.length > 0) {
          // Store the restored data in originalExtractedData so it persists
          sessionStorage.setItem('originalExtractedData', JSON.stringify(validResult));
          dispatch(setExtractedData(validResult));
          if (validResult[0]?.ocr_input_id) {
            dispatch(setOcrInputID(validResult[0].ocr_input_id));
          }
          dispatch(setStepsCompleted(true));
          
          // Clear the documentReadyReceived flag but keep lastJobId
          sessionStorage.removeItem('documentReadyReceived');
          setShowRestoreButton(false);
          toast.success('Processed data restored successfully');
          return { success: true, data: validResult };
        }
      }
    }

    // If we get here, nothing worked
    throw new Error('No data available to restore');
  } catch (error) {
    sessionStorage.removeItem('lastJobId');
    setShowRestoreButton(false);
    toast.error(error.message || 'No data available to restore');
    return { success: false, error: error.message };
  }
};

// deletes the current job from the API
export const deleteCurrentJob = async () => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      return true; 
    }

    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/queue/current`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'accept': 'application/json'
      }
    });

    // If there's no current job (404), that's fine
    if (response.ok || response.status === 404) {
      return true;
    }
    return false;
  } catch (error) {
    // Silently fail - we'll still reload the page
    return false;
  }
};

// clears all job-related sessionStorage items
export const clearJobSessionStorage = () => {
  sessionStorage.removeItem('hasCompletedJob');
  sessionStorage.removeItem('lastJobId');
  // originalExtractedData is deprecated - data is in workflow tabs
  sessionStorage.removeItem('documentReadyReceived');
};

// resets the dashboard by clearing sessionStorage and reloading the page
export const resetDashboard = async () => {
  // Clear all pdfs from localStorage when dashboard is reset
  // PDFs are now stored only in memory (workflow file cache) - no localStorage cleanup needed
  clearJobSessionStorage();
  await deleteCurrentJob();
  window.location.reload();
};

export const restoreWorkflowFiles = ({ workflowSessionId, dispatch, store }) => {
  if (!workflowSessionId) {
    return { success: false, fileCount: 0 };
  }

  const cachedFiles = getWorkflowFiles(workflowSessionId);
  const fileCount = cachedFiles?.length || 0;

  if (fileCount > 0) {
    // Batch all dispatches together for better performance
    dispatch({ type: 'CLEAR_DOCUMENTS' });
    dispatch({ type: 'SET_UPLOADED_FILES', payload: cachedFiles });
    dispatch({ type: 'ADD_DOCUMENTS', payload: cachedFiles });
    dispatch({ type: 'UPLOAD_COMPLETE' });
  } else {
    // No files - ensure Redux is empty
    dispatch({ type: 'RESET_UPLOAD_STATUS' });
  }

  return { success: true, fileCount };
};

export const clearWorkflowReduxState = ({
  workflowSessionId,
  dispatch,
  setExtractedData,
  setEditableData,
  setStepsCompleted,
  updateWorkflowSessionState
}) => {
  // Batch all dispatches together
  dispatch(setExtractedData(null));
  dispatch(setEditableData(null));
  dispatch(setStepsCompleted(false));
  dispatch(setDeclarationDirty(false));
  dispatch(clearValidationErrors());
  dispatch({ type: 'CLEAR_DOCUMENTS' });
  dispatch({ type: 'SET_UPLOADED_FILES', payload: [] });
  dispatch({ type: 'RESET_UPLOAD_STATUS' });
  
  // CRITICAL: Clear preparedJson from global Redux state when switching workflows This prevents TARIC JSON from previous workflow persisting
  dispatch({ type: 'RESET_PREPARED_JSON' });
  
  // CRITICAL: Clear selections from global Redux state when switching workflows This prevents selections from previous workflow persisting
  dispatch({ type: 'RESET_SELECTED_SENDER' });
  dispatch({ type: 'RESET_SELECTED_RECEIVER' });
  dispatch({ type: 'RESET_SELECTED_AGENT' });

  // Clear errors on the workflow we are switching TO (per-workflow session slice)
  if (workflowSessionId) {
    dispatch(updateWorkflowSessionState(workflowSessionId, {
      validationErrors: [],
      taricJsonErrors: null,
    }));
  }
};

export const checkFilesSync = ({ workflowSessionId, reduxFiles = [] }) => {
  if (!workflowSessionId) {
    return { inSync: true, cacheFiles: [], reduxFiles: [] };
  }

  const cacheFiles = getWorkflowFiles(workflowSessionId);
  const reduxFilesArray = Array.isArray(reduxFiles) 
    ? reduxFiles.filter(f => f instanceof File)
    : [];

  // Fast comparison using Sets
  const cacheFileNames = new Set(cacheFiles.map(f => f.name));
  const reduxFileNames = new Set(reduxFilesArray.map(f => f.name));

  const inSync = cacheFileNames.size === reduxFileNames.size &&
    Array.from(cacheFileNames).every(name => reduxFileNames.has(name)) &&
    Array.from(reduxFileNames).every(name => cacheFileNames.has(name));

  return { inSync, cacheFiles, reduxFiles: reduxFilesArray };
};

export const syncReduxWithCache = ({ workflowSessionId, reduxFiles, dispatch }) => {
  const { inSync, cacheFiles } = checkFilesSync({ workflowSessionId, reduxFiles });

  if (inSync) {
    return false; // Already in sync, no action needed
  }

  // Out of sync - update Redux to match cache
  if (cacheFiles.length > 0) {
    dispatch({ type: 'CLEAR_DOCUMENTS' });
    dispatch({ type: 'SET_UPLOADED_FILES', payload: cacheFiles });
    dispatch({ type: 'ADD_DOCUMENTS', payload: cacheFiles });
    dispatch({ type: 'UPLOAD_COMPLETE' });
  } else {
    dispatch({ type: 'SET_UPLOADED_FILES', payload: [] });
    dispatch({ type: 'CLEAR_DOCUMENTS' });
    dispatch({ type: 'RESET_UPLOAD_STATUS' });
  }

  return true; // Sync was performed
};
